import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

/**
 * 長い本文を「畳んだ見た目」のまま全文まで届かせるためのポップオーバー。
 *
 * 工数のメモは MCP 側の AI が書くので、改行入り・箇条書き・URL 混じりの
 * そこそこ長い文章になる。一覧に全部載せると表が壊れ、載せないと
 * 締めのときに読めない。そこで一覧は 1 行に畳み、全文はここで出す。
 *
 * **ホバーだけに頼らない。** ホバー / キーボードのフォーカス / タップの
 * 3 つで開く。閉じるのはポインタが外れたとき・フォーカスが外れたとき・
 * Escape・外側のタップ。
 *
 * - トリガーは children にそのまま渡す。既にフォーカスできる要素
 *   (グリッドのマスの button など) を包めば、キーボードの到達性は
 *   そちらが持っているものをそのまま使える。
 * - フォーカスで開くのは `:focus-visible` のときだけ。マウスで押した
 *   ときまで開いたままにすると、開いた先の UI に被って邪魔になる。
 * - パネルは `createPortal` で body に出し、位置は fixed で置く。
 *   グリッドは `overflow-x-auto` の中にあるので、absolute だと
 *   スクロール枠に切られてしまう。
 */

/** パネルの最大幅 (px)。読み幅としてこれ以上広げない。 */
const PANEL_MAX_WIDTH = 384;
/** トリガーとの隙間と、画面端に残す余白 (px)。 */
const GAP = 4;
const MARGIN = 8;

/** トリガーの矩形からパネルの位置を決める。下に入らなければ上に出す。 */
function place(trigger: DOMRect): CSSProperties {
  const width = Math.min(PANEL_MAX_WIDTH, window.innerWidth - MARGIN * 2);
  const left = Math.min(Math.max(MARGIN, trigger.left), window.innerWidth - width - MARGIN);
  const below = window.innerHeight - trigger.bottom;
  return below < 160 && trigger.top > below
    ? { left, width, bottom: window.innerHeight - trigger.top + GAP }
    : { left, width, top: trigger.bottom + GAP };
}

export function NotePopover({
  content,
  children,
  className,
  dismissOnClick = false,
}: {
  /** パネルの中身 */
  content: ReactNode;
  /** トリガー。フォーカスできる要素を渡すこと */
  children: ReactNode;
  className?: string;
  /**
   * 押したときに開くのではなく畳む。
   * トリガー自身がもっと詳しいものを開く場合 (グリッドのマスは訂正フォームを
   * 開き、そこに全文の入力欄がある) は、開いたままだと被って邪魔になる。
   */
  dismissOnClick?: boolean;
}) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const open = style !== null;

  const show = useCallback(() => {
    const anchor = anchorRef.current;
    if (anchor) setStyle(place(anchor.getBoundingClientRect()));
  }, []);
  const hide = useCallback(() => setStyle(null), []);

  // 開いている間だけ、閉じる方の入力と位置の追従を見る。
  // Escape と外側のタップは document で受ける (フォーカスが
  // パネルの外へ移っていても閉じられるように)。
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && anchorRef.current?.contains(target)) return;
      hide();
    };
    const reposition = () => {
      const anchor = anchorRef.current;
      if (anchor) setStyle(place(anchor.getBoundingClientRect()));
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    // スクロールは入れ子の枠でも起きるので capture で拾う
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, hide]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: 対話するのは children に渡された要素 (button / link) の方。この span はホバーとフォーカスを束ねる入れ物でしかなく、role を持たせると読み上げに余計な要素が増える
    // biome-ignore lint/a11y/useKeyWithClickEvents: キーボードの経路は children の button が持つフォーカス (onFocus で開き、Escape で閉じる)。この onClick はホバーを持たないタッチ端末のためだけにある
    <span
      ref={anchorRef}
      className={cn('block', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      // マウスで押しただけのときは開かない (:focus-visible のときだけ)
      onFocus={(event) => {
        if (event.target instanceof Element && event.target.matches(':focus-visible')) show();
      }}
      onBlur={hide}
      // タッチには hover が無いので、タップでも開くようにする。
      // 「押すたびに開閉」にすると、ホバーで開いてから押した環境で閉じてしまう
      onClick={dismissOnClick ? hide : show}
    >
      {children}
      {open
        ? createPortal(
            <div
              role="tooltip"
              style={style}
              // メモは 500 文字までなので、たいていはこの高さに収まる。
              // 収まらない分はスクロールさせる (全文の入力欄は訂正フォームにある)
              className="fixed z-50 max-h-80 overflow-y-auto border border-line-bold bg-surface-raised px-3 py-2 text-xs leading-relaxed text-ink"
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

/**
 * ポップオーバーに出す本文。
 *
 * 改行をそのまま見せ、長い URL や長大な単語でも枠から溢れさせない。
 */
export function NoteBody({ note, className }: { note: string; className?: string }) {
  return <p className={cn('break-words whitespace-pre-wrap', className)}>{note}</p>;
}
